import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { SelectComponent } from "@shared/controls/select/select.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { AnnualStatementSettingsData } from "../../../../../models/settings/annual-statement-settings-data";
import {
  MeetingLocationTypeOptions,
  MeetingOnlineTypeOptions,
  NotifyWhenAllPartiesSignedDocumentOptions
} from "./edit-document-settings-modal.constant";
import { ToastrService } from "ngx-toastr";
import { YesNoControlComponent } from "@shared/controls/yes-no-control-component/yes-no-control.component";
import {
  DocumentSettingsData,
  EsigningEmailRecipients
} from '../../../../../models/settings/document-settings-data';
import { compare } from 'fast-json-patch';
import { SettingsApiService } from '@shared/services/settings-api.service';
import { combineLatestWith, finalize } from 'rxjs';
import {
  DocumentationType,
  MeetingLocationType,
  MeetingOnlineType
} from '@models/documents/document-enums';
import { Address } from '../../../../../models/address/address';
import { DocumentSelectionComponent } from '@shared/components/documents-related/document-selection/document-selection.component';
import { DocumentSelection } from '@models/documents/document-selection';
import { AddressStatus } from '../../../../../models/address/address-status.enums';
import { isDocumentVisible } from '@shared/components/documents-related/document-selection/utils/is-document-visible';
import {
  AddressFormGroupComponent
} from "../../../../../forms/reusable-form-groups/address-form-group/address-form-group.component";
import { setControlDisabled } from "../../../../../shared/utils/set-control-disabled";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { getDocumentationTypeOptions } from "../../../../../forms/utils/get-documentation-type-options";

@Component({
  selector: 'app-edit-document-settings-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModalFormComponent,
    AddressFormGroupComponent,
    SelectComponent,
    RadioComponent,
    YesNoControlComponent,
    DocumentSelectionComponent,
  ],
  templateUrl: 'edit-document-settings-modal.component.html',
  styleUrl: 'edit-document-settings-modal.component.scss'
})
export class EditDocumentSettingsModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private toastr = inject(ToastrService);
  private settingsService = inject(SettingsApiService);
  private destroyRef = inject(DestroyRef);

  @Input() documentSettings = new DocumentSettingsData();
  @Input() asSettings = new AnnualStatementSettingsData();
  @Input() initDocumentSelectionFiles: DocumentSelection[] = [];

  readonly NotifyWhenAllPartiesSignedDocumentOptions = NotifyWhenAllPartiesSignedDocumentOptions;
  readonly DocumentTypeToGenerateOptions = getDocumentationTypeOptions(DocumentationType.FormOnly);
  readonly MeetingLocationTypeOptions = MeetingLocationTypeOptions;
  readonly MeetingOnlineTypeOptions = MeetingOnlineTypeOptions;
  readonly MeetingLocationType = MeetingLocationType;

  addressStatus: AddressStatus | undefined;
  isInvalidSaveButton = false;
  isLoading = false;
  form = new FormGroup({
    // documents
    receiveEmailsForDocs: new FormControl<EsigningEmailRecipients | null>(EsigningEmailRecipients.DoNotSend),

    // annual statement
    generateAutomatically: new FormControl<boolean>(true),
    documentTypeToGenerate: new FormControl<DocumentationType>(DocumentationType.Minutes),
    addSignatoryToCompanyStatement: new FormControl<boolean>(true),
    meetingLocationType: new FormControl<MeetingLocationType>(MeetingLocationType.Registered),
    meetingOnlineType: new FormControl<MeetingOnlineType | null>(null),
    otherAddress: AddressFormGroupComponent.defineForm(),
    documentSelectionFiles: new FormControl<DocumentSelection[]>([])
  });

  ngOnInit() {
    this.activeModal.update({ modalDialogClass: 'common-modal-form-dialog overflow-y-auto' });
    this.listenMeetingLocationTypeChanges();

    this.form.patchValue({
      receiveEmailsForDocs: this.documentSettings.esigningEmailRecipients,
      generateAutomatically: this.asSettings.generateAutomatically,
      documentTypeToGenerate: this.asSettings.documentTypeToGenerate,
      addSignatoryToCompanyStatement: this.asSettings.addSignatoryToCompanyStatement,
      meetingLocationType: this.asSettings.meetingLocationType,
      meetingOnlineType: this.asSettings.meetingOnlineType,
      otherAddress: this.asSettings.otherAddress ?? new Address(),
      documentSelectionFiles: this.setDocumentSelectionFiles()
    });
  }

  onDataStatusChange(addressStatus: AddressStatus | undefined): void {
    this.addressStatus = addressStatus;
    if (addressStatus === AddressStatus.WARNING && this.form.controls.meetingLocationType.value == MeetingLocationType.Other) {
      this.isInvalidSaveButton = true;
    } else {
      this.isInvalidSaveButton = false;
    }
  }

  confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastr.error('Please fill required fields', 'Error');
      return;
    }

    const documentSettings = new DocumentSettingsData({ esigningEmailRecipients: this.form.value.receiveEmailsForDocs! });
    const documentSettingsPatch = compare(this.documentSettings, documentSettings);

    const asSettings = new AnnualStatementSettingsData({
      generateAutomatically: this.form.value.generateAutomatically!,
      documentTypeToGenerate: this.form.value.documentTypeToGenerate!,
      addSignatoryToCompanyStatement: this.form.value.addSignatoryToCompanyStatement!,
      meetingLocationType: this.form.value.meetingLocationType!,
      meetingOnlineType: this.form.value.meetingOnlineType!,
      otherAddress: this.form.value.otherAddress ? new Address(this.form.value.otherAddress as Partial<Address>) : null,
      documentSelectionFiles: this.form.value.documentSelectionFiles!
    });

    if (asSettings.meetingLocationType !== MeetingLocationType.Other) {
      asSettings.otherAddress = null;
    } else {
      asSettings.otherAddress = this.form.controls.otherAddress.getRawValue() as Address;
    }
    const asSettingsPatch = compare(this.asSettings, asSettings);

    this.isLoading = true;

    const documentPatch$ = this.settingsService.patchDocumentSettings(documentSettingsPatch);
    const asPatch$ = this.settingsService.patchAnnualStatementSettings(asSettingsPatch);

    documentPatch$
      .pipe(
        combineLatestWith(asPatch$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastr.success('Data saved successfully', 'Success');
          this.activeModal.close();
        },
        error: () => this.toastr.error('Error happened while saving data', 'Error')
      });
  }

  public onDocumentSelectionChange(documentSelections: DocumentSelection[]): void {
    this.form.value.documentSelectionFiles = documentSelections;
  }

  public onDocumentationTypeChange(): void {
    this.form.value.documentSelectionFiles = this.setDocumentSelectionFiles();
  }

  onMeetingAddressTypeSelect(type: MeetingLocationType): void {
    setControlDisabled(this.form.controls.otherAddress, type !== MeetingLocationType.Other);
  }

  setDocumentSelectionFiles(): DocumentSelection[] {
    const selectionList = this.initDocumentSelectionFiles.filter(
      (documentSelection) => isDocumentVisible(documentSelection, this.form.value.documentTypeToGenerate!)
    );

    if (this.asSettings.documentSelectionFiles.length !== 0) {
      selectionList.forEach((file) => {
        const existFile = this.asSettings.documentSelectionFiles.find((documentSelectionFile) => documentSelectionFile.fileName === file.fileName || documentSelectionFile.fileId === file.fileId);
        if (existFile) {
          file.sequence = existFile.sequence;
          file.included = existFile.included;
        }
      });

      const maxSequence = Math.max(...this.asSettings.documentSelectionFiles.map((documentSelectionFile) => documentSelectionFile.sequence || 0));

      const missingFiles = selectionList.filter((file) =>
        !this.asSettings.documentSelectionFiles.some(f => f.fileName === file.fileName)
      );

      missingFiles.forEach((file, index) => {
        file.sequence = maxSequence + index + 1;
      });
    }

    return selectionList.sort((a, b) => a.sequence - b.sequence);
  }

  listenMeetingLocationTypeChanges(): void {
    this.form.controls.meetingLocationType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((meetingLocationType) => {
        setControlDisabled(this.form.controls.otherAddress, meetingLocationType != MeetingLocationType.Other);
      });
  }

  close(): void {
    this.activeModal.dismiss();
  }
}
